<template>
  <main class="grow alt-hub">
    <!-- HERO (same dark band idiom as the detail pages) -->
    <header class="alt-hub-hero">
      <div class="alt-hub-shell">
        <nav class="alt-hub-crumb" aria-label="Breadcrumb">
          <NuxtLink :to="localePath('/')">Home</NuxtLink>
          <span aria-hidden="true">/</span>
          <span aria-current="page">Alternatives</span>
        </nav>
        <h1>Software alternatives, compared honestly.</h1>
        <p class="alt-hub-lede">
          When a tool raises prices, removes a feature, or stops fitting the way you
          work, these pages lay out the real options — current prices, what each tool
          does better, where each one falls short, and who should stay put.
        </p>
        <div class="hub-actions">
          <a
            :href="addReferralToUrl('https://app.workspace369.com/signup')"
            data-conversion-event="start_free_click"
            data-conversion-label="Alternatives hub start free"
            data-conversion-location="alternatives-hub-hero"
          >
            Start free trial
          </a>
          <!-- Terms directly under the trial button (B1.1): on phones the buttons stack, so the line sits between them;
               from 640px it takes its own row under both. -->
          <TrialDisclosure on-dark />
          <NuxtLink
            :to="localePath('/pricing')"
            data-conversion-event="pricing_click"
            data-conversion-label="Alternatives hub compare plans"
            data-conversion-location="alternatives-hub-hero"
          >Compare plans</NuxtLink>
        </div>
        <p class="alt-hub-methodology">
          <span>Methodology —</span> Every comparison checks the vendor’s own pricing
          page and says when. Quotes come from public threads and review sites, with
          links. Workspace369 is our product; it appears in these tables under the
          same rules as every other tool, including where it is not the best fit.
        </p>
      </div>
    </header>

    <section class="alt-hub-library" aria-label="Alternatives comparisons">
      <div class="alt-hub-shell">
        <h2 class="alt-hub-popular-title">Popular searches</h2>
        <ul class="alt-hub-popular">
          <li>
            <NuxtLink
              :to="localePath('/alternatives/honeybook')"
              data-conversion-event="comparison_click"
              data-conversion-label="Alternatives hub popular best HoneyBook alternative for freelancers"
              data-conversion-location="alternatives-hub-popular"
            >best HoneyBook alternative for freelancers</NuxtLink>
          </li>
          <li>
            <NuxtLink
              :to="localePath('/alternatives/honeybook')"
              data-conversion-event="comparison_click"
              data-conversion-label="Alternatives hub popular cheapest HoneyBook alternative"
              data-conversion-location="alternatives-hub-popular"
            >cheapest HoneyBook alternative</NuxtLink>
          </li>
          <li>
            <NuxtLink
              :to="localePath('/alternatives/honeybook')"
              data-conversion-event="comparison_click"
              data-conversion-label="Alternatives hub popular HoneyBook alternative for a small agency team"
              data-conversion-location="alternatives-hub-popular"
            >HoneyBook alternative for a small agency team</NuxtLink>
          </li>
          <li>
            <NuxtLink
              :to="localePath('/alternatives/moxie')"
              data-conversion-event="comparison_click"
              data-conversion-label="Alternatives hub popular Moxie (Hectic) alternative"
              data-conversion-location="alternatives-hub-popular"
            >Moxie (Hectic) alternative</NuxtLink>
          </li>
          <li>
            <NuxtLink
              :to="localePath('/alternatives/plutio')"
              data-conversion-event="comparison_click"
              data-conversion-label="Alternatives hub popular Plutio alternative"
              data-conversion-location="alternatives-hub-popular"
            >Plutio alternative</NuxtLink>
          </li>
          <li>
            <NuxtLink
              :to="localePath('/alternatives/suitedash')"
              data-conversion-event="comparison_click"
              data-conversion-label="Alternatives hub popular SuiteDash alternative"
              data-conversion-location="alternatives-hub-popular"
            >SuiteDash alternative</NuxtLink>
          </li>
        </ul>
        <ul class="alt-hub-cards">
          <li v-for="entry in entries" :key="entry.slug">
            <NuxtLink
              :to="localePath(`/alternatives/${entry.slug}`)"
              class="alt-hub-card"
              data-conversion-event="comparison_click"
              :data-conversion-label="`Alternatives hub card ${entry.eyebrow}`"
              data-conversion-location="alternatives-hub-cards"
            >
              <p class="alt-hub-card-top">
                <span>{{ entry.eyebrow }}</span>
                <span>{{ alternativeCount(entry) }} tools compared</span>
              </p>
              <h2>{{ entry.eyebrow }}, honestly compared</h2>
              <p>{{ entry.metaDescription }}</p>
              <p class="alt-hub-card-bottom">
                <span>Updated {{ entry.updated }}</span>
                <strong>Read the comparison →</strong>
              </p>
            </NuxtLink>
          </li>
        </ul>
      </div>
    </section>

    <!-- Each page's first FAQ is its buyer question, answered with checked prices. Search and AI
         answers read this hub more than the detail pages, so the answers are repeated here. -->
    <section class="alt-hub-answers" aria-labelledby="alt-hub-answers-title">
      <div class="alt-hub-shell">
        <h2 id="alt-hub-answers-title" class="alt-hub-popular-title">The short answer for each tool</h2>
        <ul class="alt-hub-answer-list">
          <li v-for="entry in answerEntries" :key="entry.slug">
            <h3>{{ entry.faqs[0].q }}</h3>
            <p>{{ entry.faqs[0].a }}</p>
            <NuxtLink
              :to="localePath(`/alternatives/${entry.slug}`)"
              data-conversion-event="comparison_click"
              :data-conversion-label="`Alternatives hub answer ${entry.eyebrow}`"
              data-conversion-location="alternatives-hub-answers"
            >Read the comparison →</NuxtLink>
          </li>
        </ul>
      </div>
    </section>
    <Cta03 />
    <PhoneStickyCta label="Alternatives hub" :location="$route.path" watch=".alt-hub-hero .hub-actions" />
  </main>
</template>

<script setup>
import { alternativesPages } from '~/data/alternatives-pages';
import { localizedUrl } from '~/data/locales';
import { ogImagePathForRoute } from '~/utils/og-image';

const { canonicalLocaleCode, isCurrentPageIndexable, localePath } = useWorkspaceLocale();
const { addReferralToUrl } = useReferral();
const siteUrl = 'https://workspace369.com';

const entries = alternativesPages;
const alternativeCount = (entry) => entry.tools.filter((tool) => !tool.baseline).length;
// Only pages whose first FAQ is a buyer question ("best X alternative…"); price-only openers stay on their page.
const answerEntries = entries.filter((entry) => /\balternatives?\b/i.test(entry.faqs?.[0]?.q || ''));

const canonicalUrl = localizedUrl(siteUrl, '/alternatives', canonicalLocaleCode.value);
const title = 'Software Alternatives, Compared Honestly | Workspace369';
const description =
  'Honest alternatives pages for tools service businesses switch between: current prices with checked-on dates, sourced user quotes, and who should stay put.';
const image = `${siteUrl}${ogImagePathForRoute('/alternatives')}`;

useSeoMeta({
  title,
  description,
  ogType: 'website',
  ogTitle: title,
  ogDescription: description,
  ogUrl: canonicalUrl,
  ogImage: image,
  twitterCard: 'summary_large_image',
  twitterTitle: title,
  twitterDescription: description,
  twitterImage: image,
});

useHead({
  meta: [
    {
      key: 'robots',
      name: 'robots',
      content: isCurrentPageIndexable.value ? 'index, follow' : 'noindex, follow',
    },
  ],
  link: [{ key: 'canonical', rel: 'canonical', href: canonicalUrl }],
  script: [
    {
      key: 'alternatives-hub-schema',
      type: 'application/ld+json',
      children: JSON.stringify({
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'CollectionPage',
            '@id': `${canonicalUrl}#webpage`,
            name: title,
            description,
            url: canonicalUrl,
          },
          {
            '@type': 'ItemList',
            '@id': `${canonicalUrl}#comparisons`,
            itemListElement: entries.map((entry, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              name: entry.eyebrow,
              url: `${siteUrl}/alternatives/${entry.slug}`,
            })),
          },
          {
            '@type': 'BreadcrumbList',
            '@id': `${canonicalUrl}#breadcrumb`,
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: `${siteUrl}/`,
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'Alternatives',
                item: canonicalUrl,
              },
            ],
          },
        ],
      }),
    },
  ],
});
</script>

<style scoped>
.alt-hub {
  background: #ffffff;
  color: #0f172a;
}

.alt-hub-shell {
  width: min(100% - 2rem, 72rem);
  margin-inline: auto;
}

/* Hero: same dark band as the detail pages */
.alt-hub-hero {
  background: #020617;
  padding: 6.5rem 0 3.5rem;
}

@media (min-width: 900px) {
  .alt-hub-hero {
    padding: 10rem 0 5rem;
  }
}

.alt-hub-crumb {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  color: #94a3b8;
  font-size: 0.78rem;
  font-weight: 700;
}

.alt-hub-crumb a {
  display: inline-flex;
  min-height: 44px;
  align-items: center;
  margin-block: -0.6rem;
  color: #94a3b8;
  text-decoration: none;
}

.alt-hub-crumb a:hover {
  color: #67e8f9;
}

.alt-hub-crumb span[aria-hidden] {
  color: #475569;
}

.alt-hub-hero h1 {
  margin-top: 1.75rem;
  max-width: 46rem;
  color: #ffffff;
  font-size: clamp(2.1rem, 5vw, 3.5rem);
  font-weight: 880;
  letter-spacing: 0;
  line-height: 1.05;
  text-wrap: balance;
}

.alt-hub-lede {
  margin-top: 1.25rem;
  max-width: 44rem;
  color: #cbd5e1;
  font-size: clamp(1rem, 1.8vw, 1.13rem);
  line-height: 1.7;
}

.hub-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 1.6rem;
}

.hub-actions a {
  display: inline-flex;
  min-height: 2.9rem;
  min-width: 9.5rem;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  padding: 0.7rem 1.1rem;
  font-size: 0.92rem;
  font-weight: 800;
  text-decoration: none;
}

/* max-width none: with its default 34rem cap the line can fit beside the buttons on wide screens instead of under them. */
.hub-actions :deep(.trial-disclosure) {
  flex: 0 0 100%;
  max-width: none;
  margin: 0;
}

@media (min-width: 640px) {
  .hub-actions :deep(.trial-disclosure) {
    order: 2;
  }
}

.hub-actions a:first-child {
  background: #0e7490;
  color: #fff;
}

.hub-actions a:first-child:hover {
  background: #0b6a84;
}

.hub-actions a:last-child {
  border: 1px solid rgb(255 255 255 / 0.22);
  color: #fff;
}

.alt-hub-methodology {
  margin-top: 1.4rem;
  max-width: 44rem;
  color: #94a3b8;
  font-size: 0.8rem;
  line-height: 1.65;
}

.alt-hub-methodology span {
  color: #cbd5e1;
  font-weight: 700;
}

/* Card library: research-card idiom — hairline, radius, hover border only */
.alt-hub-library {
  padding: 3.5rem 0 3.5rem;
}

.alt-hub-popular-title {
  margin: 0 0 0.75rem;
  color: #0f172a;
  font-size: 1.1rem;
  font-weight: 800;
}

.alt-hub-popular {
  display: flex;
  flex-wrap: wrap;
  gap: 0 1.25rem;
  margin: 0 0 2rem;
  padding: 0;
  list-style: none;
}

.alt-hub-popular a {
  /* 44px tap targets on phones (the links were 28px). */
  display: inline-flex;
  min-height: 44px;
  align-items: center;
  color: #0e7490;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.alt-hub-cards {
  display: grid;
  gap: 1.25rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

@media (min-width: 760px) {
  .alt-hub-cards {
    grid-template-columns: 1fr 1fr;
  }
}

.alt-hub-card {
  display: flex;
  flex-direction: column;
  height: 100%;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #ffffff;
  padding: 1.75rem;
  text-decoration: none;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.alt-hub-card:hover {
  border-color: #83c3d8;
  box-shadow: 0 8px 22px rgb(18 49 40 / 0.03);
}

.alt-hub-card-top {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin: 0;
  color: #0e7490;
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.alt-hub-card h2 {
  margin: 1.1rem 0 0.7rem;
  color: #0f172a;
  font-size: 1.4rem;
  font-weight: 800;
  letter-spacing: -0.01em;
  line-height: 1.3;
}

.alt-hub-card > p:not(.alt-hub-card-top):not(.alt-hub-card-bottom) {
  margin: 0;
  color: #475569;
  font-size: 0.88rem;
  line-height: 1.7;
}

.alt-hub-card-bottom {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
  margin: 1.5rem 0 0;
  border-top: 1px solid #e8eef4;
  padding-top: 1rem;
  color: #64748b;
  font-size: 0.74rem;
}

.alt-hub-card-bottom strong {
  color: #0e7490;
  font-weight: 700;
}

/* Phones: tighter cards, every word kept (19 cards went from 12.6 to 8.9 screens at 390px, 125% text).
   The tool label repeats the heading's first words, so only "N tools compared" stays in the top row. */
@media (max-width: 639px) {
  .alt-hub-cards {
    gap: 0.75rem;
  }
  .alt-hub-card {
    padding: 1.1rem 1.15rem;
  }
  .alt-hub-card-top {
    justify-content: flex-start;
  }
  .alt-hub-card-top span:first-child {
    display: none;
  }
  .alt-hub-card h2 {
    margin: 0.55rem 0 0.4rem;
    font-size: 1.15rem;
    line-height: 1.25;
  }
  .alt-hub-card > p:not(.alt-hub-card-top):not(.alt-hub-card-bottom) {
    line-height: 1.55;
  }
  .alt-hub-card-bottom {
    margin-top: 0.8rem;
    padding-top: 0.65rem;
  }
}

.alt-hub-answers {
  padding: 0 0 5rem;
}

.alt-hub-answer-list {
  margin: 0;
  padding: 0;
  max-width: 50rem;
  list-style: none;
  border-top: 1px solid #e2e8f0;
}

.alt-hub-answer-list li {
  border-bottom: 1px solid #e2e8f0;
  padding: 1.4rem 0;
}

.alt-hub-answer-list h3 {
  margin: 0;
  color: #0f172a;
  font-size: 1.05rem;
  font-weight: 800;
  line-height: 1.4;
}

.alt-hub-answer-list p {
  margin: 0.5rem 0 0.6rem;
  color: #475569;
  font-size: 0.92rem;
  line-height: 1.7;
}

.alt-hub-answer-list a {
  display: inline-flex;
  min-height: 44px;
  align-items: center;
  margin-bottom: -0.6rem;
  color: #0e7490;
  font-size: 0.82rem;
  font-weight: 700;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.alt-hub-answer-list a:focus-visible,
.alt-hub-card:focus-visible,
.alt-hub-hero a:focus-visible {
  outline: 2px solid #1ba7ce;
  outline-offset: 3px;
}

/* Dark mode (site toggle): same palette as the compare hub and the alternatives detail pages. */
.dark .alt-hub { background: #020617; color: #f1f5f9; }
.dark .alt-hub-popular-title,
.dark .alt-hub-card h2,
.dark .alt-hub-answer-list h3 { color: #f1f5f9; }
.dark .alt-hub-popular a,
.dark .alt-hub-card-top,
.dark .alt-hub-card-bottom strong,
.dark .alt-hub-answer-list a { color: #67e8f9; }
.dark .alt-hub-card { border-color: #1f2b3d; background: #0b1220; }
.dark .alt-hub-card:hover { border-color: #22b8dd; }
.dark .alt-hub-card > p:not(.alt-hub-card-top):not(.alt-hub-card-bottom),
.dark .alt-hub-answer-list p { color: #a5b4c6; }
.dark .alt-hub-card-bottom { border-color: #1f2b3d; color: #94a3b8; }
.dark .alt-hub-answer-list,
.dark .alt-hub-answer-list li { border-color: #1f2b3d; }

@media (prefers-reduced-motion: reduce) {
  .alt-hub-card {
    transition: none;
  }
}
</style>
